import axios from 'axios';
import { toast } from 'react-hot-toast';

const API_BASE_URL = 'https://api.hananmanan.wecodeforyou.io';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    
    const message = error.response?.data?.message || 'حدث خطأ غير متوقع';
    toast.error(message);
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getCurrentUser: () => api.get('/users/profile'),
};

// Events API
export const eventsAPI = {
  getAll: () => api.get('/events'),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.patch(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  getUpcoming: () => api.get('/events/upcoming'),
};

// Halls API
export const hallsAPI = {
  getAll: () => api.get('/bookings/halls'),
  getById: (id) => api.get(`/bookings/halls/${id}`),
  create: (data) => api.post('/bookings/halls', data),
  update: (id, data) => api.patch(`/bookings/halls/${id}`, data),
  delete: (id) => api.delete(`/bookings/halls/${id}`),
  getAvailability: (id, date) => api.get(`/bookings/halls/${id}/availability?date=${date}`),
};

// Bookings API
export const bookingsAPI = {
  getAll: () => api.get('/bookings'),
  getById: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  update: (id, data) => api.patch(`/bookings/${id}`, data),
  delete: (id) => api.delete(`/bookings/${id}`),
  getUserBookings: (userId) => api.get(`/bookings/user/${userId}`),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Notifications API
export const notificationsAPI = {
  create: (data) => api.post('/notifications', data),
  send: (data) => api.post('/notifications', data),
  getAll: () => api.get('/notifications'),
  sendBroadcast: (title, message) => api.post('/notifications/broadcast', { title, message }),
};

// Gallery API
export const galleryAPI = {
  getAll: (queryString = '') => {
    return api.get(`/gallery${queryString}`);
  },
  getById: (id) => api.get(`/gallery/${id}`),
  create: (data) => api.post('/gallery', data),
  update: (id, data) => api.patch(`/gallery/${id}`, data),
  delete: (id) => api.delete(`/gallery/${id}`),
  getByCategory: () => api.get('/gallery/categories/all'),

  getAllCategories: () => api.get('/gallery/categories/list'),
  getCategoryById: (id) => api.get(`/gallery/categories/${id}`),
  createCategory: (data) => api.post('/gallery/categories', data),
  updateCategory: (id, data) => api.patch(`/gallery/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/gallery/categories/${id}`),
};

// File upload helper
export const uploadFile = async (file, folder = 'uploads') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  try {
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.url;
  } catch (error) {
    throw new Error('فشل في رفع الملف');
  }
};

export default api;